import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { organizationsAccessListOptions } from 'src/api/main'

import type { WorkspaceUser } from '../types'

type Args = {
  workspaceId: number
  userId: number
  enabled?: boolean
}

// Resolves a single workspace member by user id via the server-side `user`
// filter. We must NOT scan the workspace-wide list here: the backend caps the
// access-list page size below large orgs' member counts, so any member past
// the cap was silently unresolvable (stuck on "User not found"). The
// `{ organization, user }` query returns exactly that member's access record.
// The `organization` key still matches `workspaceUsersListQueryKey`'s
// partial-deep-equal prefix, so list mutations keep invalidating this query.
export const useWorkspaceUser = ({
  workspaceId,
  userId,
  enabled = true
}: Args) => {
  const query = useQuery({
    ...organizationsAccessListOptions({
      query: { organization: workspaceId, user: userId }
    }),
    enabled: enabled && Number.isFinite(workspaceId) && Number.isFinite(userId),
    refetchOnMount: 'always',
    placeholderData: keepPreviousData
  })

  const record = useMemo<WorkspaceUser | undefined>(
    () => query.data?.results.find((u) => u.user.id === userId),
    [query.data, userId]
  )

  return {
    record,
    // Placeholder data is the previously viewed member, so `record` resolves to
    // nothing while the new one is in flight. Reporting that as loaded is what
    // flashed "User not found" on every navigation between two members.
    isLoading: query.isLoading || query.isPlaceholderData,
    isError: query.isError,
    refetch: query.refetch
  }
}
